import React, { useEffect, useRef, useState } from "react";
import { BarChart3, ChevronDown } from "lucide-react";
import "./ChartCard.css";

/**
 * Time-resolution control for the dashboard charts.
 *
 * Hand-written rather than built on a popup component, after two approaches
 * that each failed differently: MUI's Popover placed the list away from the
 * chip and locked page scrolling behind it, and a native <select> handed the
 * list to the operating system, which knows nothing about the app's styling
 * or its dark mode.
 *
 * This is a plain list positioned inside the chip's own box, so it opens
 * under the control by construction, scrolls with the page, and is built from
 * the same tokens as everything else on the card.
 */
const GranularitySelect = ({
  value,
  options,
  resolvedLabel,
  autoLabel,
  onChange,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <span className="lc-granularity" ref={rootRef}>
      <button
        type="button"
        className={`lc-chip lc-chip--button${open ? " is-open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <BarChart3 aria-hidden="true" />
        {resolvedLabel}
        {/* On auto the chip says which resolution it settled on, with a badge
            explaining that nobody chose it. */}
        {value === "auto" && <span className="lc-chip__preset">{autoLabel}</span>}
        <ChevronDown className="lc-chip__caret" aria-hidden="true" />
      </button>

      {open && (
        <ul className="lc-granularity__menu" role="listbox">
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={`lc-granularity__item${
                  option.value === value ? " is-selected" : ""
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
};

export default GranularitySelect;
